import { useProductPicker } from "@/hooks/menu/useProductPicker";
import ProductPickerSelect from "@/components/menu/ProductPickerSelect";
import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/Input";
import { TextArea } from "@/components/Textarea";
import { Label } from "@/components/Label";
import Switch from "@/components/ui/switch";
import {
  useCreateDailyDishMutation,
  useUpdateDailyDishMutation,
} from "@/redux/services/dailyDishes/dailyDishes.api";
import { useUploadMediaMutation } from "@/redux/services/media/media.api";
import { useAppSelector } from "@/redux/features/hooks";
import {
  DailyDish,
  DailyDishFormData,
  DailyDishPayload,
} from "@/redux/services/dailyDishes/dailyDishes.type";
import toast from "react-hot-toast";
import {
  cn,
  convertPriceFromCent,
  convertPriceToCent,
  extractTimeFromDateTime,
} from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import {
  identityImagePayload,
  identityPricePayload,
  identityTextPayload,
  refillOnProductChange,
} from "@/lib/catalogueIdentity";

const DAY_LABELS = [
  { value: 0, label: "Dim" },
  { value: 1, label: "Lun" },
  { value: 2, label: "Mar" },
  { value: 3, label: "Mer" },
  { value: 4, label: "Jeu" },
  { value: 5, label: "Ven" },
  { value: 6, label: "Sam" },
];

const Field = ({ children }: { children: React.ReactNode }) => (
  <div className="[&>label]:block [&>label]:mb-1 [&>label]:text-sm [&>label]:text-card-foreground">
    {children}
  </div>
);

const Section = ({
  title,
  defaultOpen = true,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <section>
        <CollapsibleTrigger className="w-full flex items-center justify-between py-2 border-b border-border text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-card-foreground transition-colors">
          <span>{title}</span>
          <ChevronDown
            className={cn("h-4 w-4 transition-transform", open && "rotate-180")}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4 space-y-4">
          {children}
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  dailyDish?: DailyDish | null;
}

const DailyDishFormDrawer = ({ isOpen, onClose, dailyDish }: Props) => {
  const [createDailyDish, { isLoading: isCreating }] =
    useCreateDailyDishMutation();
  const [updateDailyDish, { isLoading: isUpdating }] =
    useUpdateDailyDishMutation();
  const [uploadMedia] = useUploadMediaMutation();

  const isEditMode = Boolean(dailyDish);
  const isLoading = isCreating || isUpdating;

  // A plat du jour may point at a catalogue dish (Product) so its recipe and
  // its sales roll up to that dish; it can also stand alone.
  const restaurant = useAppSelector((state) => state.user.currentRestaurant);
  const [selectedProductId, setSelectedProductId] = useState("");
  // Server-side search, 50 dishes a page (SFRESTO-166).
  const picker = useProductPicker({
    restaurantId: restaurant?.id,
    enabled: isOpen,
    selectedId: selectedProductId || dailyDish?.product?.id,
  });
  const { findProduct } = picker;

  const [formData, setFormData] = useState<DailyDishFormData>({
    name: "",
    description: "",
    price: 0,
    daysOfWeek: [],
    startTime: "",
    endTime: "",
    isActive: true,
    sortOrder: 0,
    tags: [],
  });

  const [priceInput, setPriceInput] = useState<number>(0);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [tagsInput, setTagsInput] = useState<string>("");

  useEffect(() => {
    if (dailyDish) {
      setFormData({
        name: dailyDish.name || "",
        description: dailyDish.description || "",
        price: dailyDish.price || 0,
        daysOfWeek: dailyDish.daysOfWeek || [],
        startTime: dailyDish.startTime
          ? extractTimeFromDateTime(dailyDish.startTime)
          : "",
        endTime: dailyDish.endTime
          ? extractTimeFromDateTime(dailyDish.endTime)
          : "",
        isActive: dailyDish.isActive ?? true,
        sortOrder: dailyDish.sortOrder || 0,
        tags: dailyDish.tags || [],
      });
      setPriceInput(convertPriceFromCent(dailyDish.price));
      setTagsInput((dailyDish.tags || []).join(", "));
      setImagePreview(dailyDish.image?.url ?? "");
      setImageFile(null);
      setSelectedProductId(dailyDish.product?.id ?? "");
    } else {
      setFormData({
        name: "",
        description: "",
        price: 0,
        daysOfWeek: [],
        startTime: "",
        endTime: "",
        isActive: true,
        sortOrder: 0,
        tags: [],
      });
      setPriceInput(0);
      setImagePreview("");
      setImageFile(null);
      setTagsInput("");
      setSelectedProductId("");
    }
  }, [dailyDish, isOpen]);

  // Picking a dish fills in whatever the manager has not typed yet (or what
  // still shows the previous dish), so the usual flow is "pick the dish,
  // adjust today's price". Unpicking keeps the text: the dish was only a
  // starting point.
  const handleProductChange = (productId: string) => {
    const previous = findProduct(selectedProductId);
    setSelectedProductId(productId);
    const product = findProduct(productId);
    if (!product) return;
    setFormData((prev) => ({
      ...prev,
      name: refillOnProductChange(
        prev.name ?? "",
        previous?.title,
        product.title
      ),
      description: refillOnProductChange(
        prev.description ?? "",
        previous?.description,
        product.description
      ),
    }));
    if (
      !priceInput ||
      (previous && priceInput === convertPriceFromCent(previous.basePrice))
    ) {
      setPriceInput(convertPriceFromCent(product.basePrice));
    }
    if (
      !imageFile &&
      (!imagePreview ||
        (previous?.image?.url && imagePreview === previous.image.url))
    ) {
      setImagePreview(product.image?.url ?? "");
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const toggleDay = (day: number) => {
    setFormData((prev) => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter((d) => d !== day)
        : [...prev.daysOfWeek, day],
    }));
  };

  const handleTagsChange = (value: string) => {
    setTagsInput(value);
    const tagsArray = value
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag);
    setFormData((prev) => ({ ...prev, tags: tagsArray }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error("Le nom est requis");
      return;
    }
    if (formData.daysOfWeek.length === 0) {
      toast.error("Veuillez sélectionner au moins un jour");
      return;
    }

    try {
      let uploadedIri: string | undefined;
      if (imageFile) {
        const uploadFormData = new FormData();
        uploadFormData.append("file", imageFile);
        const uploadResult = await uploadMedia(uploadFormData);
        if ("data" in uploadResult && uploadResult.data) {
          uploadedIri = `/api/media_objects/${uploadResult.data.id}`;
        }
      }

      const priceCents = Math.round(convertPriceToCent(priceInput));
      const submitData: DailyDishPayload = {
        ...formData,
        price: priceCents,
        startTime: formData.startTime ? `${formData.startTime}:00` : undefined,
        endTime: formData.endTime ? `${formData.endTime}:00` : undefined,
        product: selectedProductId
          ? `/api/products/${selectedProductId}`
          : null,
      };

      if (selectedProductId) {
        // Linked to a catalogue dish: follow its name, description, price
        // and image, and write only real overrides (SFRESTO-122). The form
        // shows the values the API resolved through the Product.
        const product = findProduct(selectedProductId);
        const sameProduct = dailyDish?.product?.id === selectedProductId;
        const identity = {
          name: identityTextPayload(formData.name ?? "", {
            productValue: product ? product.title : undefined,
            initialValue: sameProduct ? dailyDish?.name : undefined,
          }),
          description: identityTextPayload(formData.description ?? "", {
            productValue: product ? (product.description ?? null) : undefined,
            initialValue: sameProduct ? dailyDish?.description : undefined,
          }),
          price: identityPricePayload(priceCents, {
            productValue: product ? product.basePrice : undefined,
            initialValue: sameProduct ? dailyDish?.price : undefined,
          }),
          image: identityImagePayload({
            uploadedIri,
            currentImageId: dailyDish?.image?.id,
            productImageId: product ? (product.image?.id ?? null) : undefined,
          }),
        };
        delete submitData.name;
        delete submitData.description;
        delete submitData.price;
        for (const key of ["name", "description", "price", "image"] as const) {
          const value = identity[key];
          // A new daily dish has nothing to clear: leave inherited fields out.
          if (value === undefined || (value === null && !isEditMode)) continue;
          submitData[key] = value as never;
        }
      } else {
        const imagePath =
          uploadedIri ??
          (dailyDish?.image
            ? `/api/media_objects/${dailyDish.image.id}`
            : undefined);
        if (imagePath) submitData.image = imagePath;
      }

      const res =
        isEditMode && dailyDish?.id
          ? await updateDailyDish({ id: dailyDish.id, data: submitData })
          : await createDailyDish(submitData);

      if ("data" in res) {
        toast.success(
          isEditMode
            ? "Plat du jour modifié avec succès"
            : "Plat du jour créé avec succès"
        );
        onClose();
      } else {
        toast.error("Une erreur s'est produite");
      }
    } catch (error) {
      console.error("Error submitting daily dish:", error);
      toast.error("Une erreur s'est produite");
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col gap-0"
      >
        <form onSubmit={handleSubmit} className="flex flex-col h-full min-h-0">
          <header className="flex-shrink-0 px-6 py-4 border-b border-border bg-card">
            <SheetTitle className="text-base">
              {isEditMode ? "Modifier le plat du jour" : "Nouveau plat du jour"}
            </SheetTitle>
            <SheetDescription hidden>
              Formulaire de création ou modification d'un plat du jour.
            </SheetDescription>
          </header>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
            <Section title="Informations">
              <Field>
                <Label label="Image" htmlFor="dailydish-image" />
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="Aperçu"
                    className="w-20 h-20 object-cover rounded mt-1 mb-2"
                  />
                )}
                <input
                  type="file"
                  id="dailydish-image"
                  className="block w-full text-sm border bg-white rounded-lg py-2 px-2 mt-1"
                  onChange={handleImageChange}
                  accept="image/*"
                />
              </Field>
              <Field>
                <Label
                  label="Plat du catalogue (optionnel)"
                  htmlFor="dailydish-product"
                />
                <ProductPickerSelect
                  id="dailydish-product"
                  picker={picker}
                  value={selectedProductId}
                  onChange={handleProductChange}
                  emptyLabel="-- Aucun (plat du jour indépendant) --"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Rattache ce plat du jour à un plat du catalogue : sa recette
                  et ses ventes lui sont associées.
                </p>
              </Field>
              <Field>
                <Input
                  label="Nom"
                  required
                  id="name"
                  type="text"
                  className="h-10 py-2 text-sm"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                />
              </Field>
              <Field>
                <Input
                  label="Prix"
                  required
                  id="price"
                  type="number"
                  min="0"
                  step="1"
                  className="h-10 py-2 text-sm"
                  value={priceInput}
                  onChange={(e) => setPriceInput(parseInt(e.target.value) || 0)}
                />
              </Field>
              <Field>
                <TextArea
                  label="Description"
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={3}
                />
              </Field>
              <Field>
                <Input
                  label="Ordre d'affichage"
                  id="sortOrder"
                  type="number"
                  className="h-10 py-2 text-sm"
                  value={formData.sortOrder}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      sortOrder: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </Field>
              <Field>
                <Input
                  label="Tags (séparés par des virgules)"
                  id="tags"
                  type="text"
                  className="h-10 py-2 text-sm"
                  value={tagsInput}
                  onChange={(e) => handleTagsChange(e.target.value)}
                  placeholder="végétarien, sans gluten, plat du chef..."
                />
              </Field>
            </Section>

            <Section title="Disponibilité">
              <Field>
                <Label label="Jours de disponibilité" required />
                <div className="flex flex-wrap gap-2 mt-1">
                  {DAY_LABELS.map(({ value, label }) => {
                    const isSelected = formData.daysOfWeek.includes(value);
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => toggleDay(value)}
                        className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                          isSelected
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border text-muted-foreground hover:border-primary/30"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field>
                  <Input
                    label="Heure de début"
                    id="startTime"
                    type="time"
                    className="h-10 py-2 text-sm"
                    value={formData.startTime}
                    onChange={(e) =>
                      setFormData({ ...formData, startTime: e.target.value })
                    }
                  />
                </Field>
                <Field>
                  <Input
                    label="Heure de fin"
                    id="endTime"
                    type="time"
                    className="h-10 py-2 text-sm"
                    value={formData.endTime}
                    onChange={(e) =>
                      setFormData({ ...formData, endTime: e.target.value })
                    }
                  />
                </Field>
              </div>
              <p className="text-xs text-muted-foreground">
                Laissez vide pour disponibilité toute la journée.
              </p>

              <div className="flex items-center gap-2 pt-1">
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(isActive) =>
                    setFormData({ ...formData, isActive })
                  }
                />
                <Label label="Actif" />
              </div>
            </Section>
          </div>

          <footer className="flex-shrink-0 px-6 py-4 border-t border-border bg-card flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "En cours..." : isEditMode ? "Modifier" : "Ajouter"}
            </Button>
          </footer>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default DailyDishFormDrawer;
